import React, { useRef } from 'react';
import { trackEvent } from '../../../shared/analytics';
import { solutionsVideo } from '../../../shared/config/solutionsContent';

/* Web-compressed (H.264 CRF 26, faststart, ~12 MB) from Master-final 2.mp4.
   The 111 MB archive is not in the repo (GitHub 100 MB file limit); re-encode
   from Relix Documentation / relix video content / Master video 50 sec. */
const MASTER_VIDEO_SRC = '/brand/solutions/master-50s-web.mp4?v=2';
const MASTER_VIDEO_POSTER = '/brand/solutions/master-50s-poster.jpg?v=2';
const MASTER_VIDEO_CHAPTERS = '/brand/solutions/master-50s-chapters.vtt?v=3';
const PROGRESS_MARKS = [25, 50] as const;

function parseChapterSeconds(time: string): number {
  const parts = time.split(':').map(Number);
  if (parts.length !== 2 || parts.some((n) => !Number.isFinite(n))) return 0;
  return parts[0] * 60 + parts[1];
}

function seekVideoTo(video: HTMLVideoElement, seconds: number): void {
  const apply = () => {
    video.currentTime = seconds;
  };
  if (video.readyState >= HTMLMediaElement.HAVE_METADATA) {
    apply();
  } else {
    video.addEventListener('loadedmetadata', apply, { once: true });
  }
  void video.play();
}

/**
 * Master overview video — sits immediately before the Forecast spotlight
 * on the public Solutions page (SPA + prerendered static twin).
 *
 * Click-to-play (RLX-FEAT-008): `preload="none"` + poster, no autoplay — the
 * video loads only on explicit intent. Chapters ship as a WebVTT track
 * and as a visible <details> transcript (no-JS and crawler friendly).
 * Timestamp buttons seek the player; they are inert without JS.
 *
 * Playback events (start / 25% / 50% / complete / chapter) feed PostHog;
 * marks fire once per mount. Handlers are inert in the prerendered static twin.
 */
export function MasterVideoSection(): React.ReactElement {
  const videoRef = useRef<HTMLVideoElement>(null);
  const firedMarks = useRef<Set<number>>(new Set());

  const onTimeUpdate = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    const video = e.currentTarget;
    if (!video.duration) return;
    const pct = (video.currentTime / video.duration) * 100;
    for (const mark of PROGRESS_MARKS) {
      if (pct >= mark && !firedMarks.current.has(mark)) {
        firedMarks.current.add(mark);
        trackEvent('solutions_video', { action: `progress_${mark}` });
      }
    }
  };

  const onChapterClick = (time: string) => {
    const video = videoRef.current;
    if (!video) return;
    seekVideoTo(video, parseChapterSeconds(time));
    trackEvent('solutions_video', { action: 'chapter', time });
    video.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  return (
    <section className="solutions-master-video" aria-label="Relix product overview video">
      <p className="solutions-master-video__eyebrow">{solutionsVideo.eyebrow}</p>
      <div className="forecast-spotlight__frame product-window">
        <span className="product-window__chip" aria-hidden="true">
          Relix · Overview
        </span>
        <video
          ref={videoRef}
          className="solutions-master-video__player"
          src={MASTER_VIDEO_SRC}
          poster={MASTER_VIDEO_POSTER}
          controls
          playsInline
          preload="none"
          title="Relix product overview"
          onPlay={() => trackEvent('solutions_video', { action: 'start' })}
          onTimeUpdate={onTimeUpdate}
          onEnded={() => trackEvent('solutions_video', { action: 'complete' })}
        >
          <track kind="chapters" src={MASTER_VIDEO_CHAPTERS} srcLang="en" />
        </video>
      </div>
      <details className="solutions-master-video__transcript">
        <summary>{solutionsVideo.transcriptLabel}</summary>
        <ol>
          {solutionsVideo.chapters.map((chapter) => (
            <li key={chapter.time}>
              <button
                type="button"
                className="solutions-master-video__chapter-time"
                onClick={() => onChapterClick(chapter.time)}
                aria-label={`Jump to ${chapter.time}, ${chapter.title}`}
              >
                {chapter.time}
              </button>
              {chapter.title}
            </li>
          ))}
        </ol>
      </details>
    </section>
  );
}
